/*
 * The parent's preferences (R-01-08) → what ParentPreferredSchedule renders.
 *
 * E1 (`courses[].preferences`) and E5 (`preferences`) answer from one of five
 * stores, named by `source`, and carry only the fields that store holds (E1
 * contract, "The preferences per source"). This flattens whichever arrived into
 * labelled rows and facts so the component renders every source the same way.
 */

import { dayLabel } from "../SchedulePicker/constants";
import { toDisplayDate } from "./timings";

const AVAILABILITY_LABELS = { weekday: "Weekdays", weekend: "Weekends", both: "Any day" };
const PREFERRED_DAYS_LABELS = { weekdays: "Weekdays", weekends: "Weekends" };

const slotLabel = (slot, studentTimezoneLabel) => {
  if (!slot?.istLabel) return "";
  const student = slot?.studentLabel
    ? ` / ${slot?.studentLabel}${studentTimezoneLabel ? ` ${studentTimezoneLabel}` : ""}`
    : "";
  return `${slot?.istLabel} IST${student}`;
};

const mentorFact = (mentor) => {
  if (!mentor?.mentorId) return null;
  const name = mentor?.name ?? "No longer in the catalogue";
  const note = mentor?.demoMentor ? " (demo mentor)" : mentor?.onlyThisMentor ? " (only this mentor)" : "";
  return { label: "Preferred mentor", value: `${name}${note}` };
};

/**
 * @param {object|null} raw - the preferences object
 * @param {string} [studentTimezoneLabel]
 * @returns {{classesPerWeek: number|null, rows: {label: string, values: string[]}[],
 *            facts: {label: string, value: string}[], isEmpty: boolean}|null}
 */
export const preferencesMapper = (raw, studentTimezoneLabel) => {
  if (!raw) return null;
  if (!raw?.source || raw?.source === "none") {
    return { classesPerWeek: null, rows: [], facts: [], isEmpty: true };
  }

  const rows = [
    ...(raw?.days ?? []).map((entry) => ({
      label: dayLabel(entry?.day),
      values: (entry?.slots ?? []).map((slot) => slotLabel(slot, studentTimezoneLabel)),
    })),
    /* partsOfDay is per day on an onboarding submission, a flat list on general
       availability. */
    ...(raw?.partsOfDay ?? [])
      .filter((entry) => typeof entry === "object")
      .map((entry) => ({ label: dayLabel(entry?.day), values: entry?.parts ?? [] })),
  ];
  const flatParts = (raw?.partsOfDay ?? []).filter((entry) => typeof entry === "string");

  const facts = [
    raw?.anyDayAnyTime ? { label: "Availability", value: "Any day, any time" } : null,
    raw?.availability
      ? { label: "Availability", value: AVAILABILITY_LABELS?.[raw?.availability] ?? raw?.availability }
      : null,
    raw?.preferredDays
      ? { label: "Preferred days", value: PREFERRED_DAYS_LABELS?.[raw?.preferredDays] ?? raw?.preferredDays }
      : null,
    raw?.daysPerWeek ? { label: "Days per week", value: String(raw?.daysPerWeek) } : null,
    flatParts?.length ? { label: "Parts of day", value: flatParts.join(", ") } : null,
    raw?.timeslots ? { label: "Time slots", value: String(raw?.timeslots) } : null,
    raw?.startDate ? { label: "Preferred start date", value: toDisplayDate(raw?.startDate) } : null,
    raw?.timezone ? { label: "Timezone", value: raw?.timezone } : null,
    mentorFact(raw?.mentor),
  ].filter(Boolean);

  return {
    classesPerWeek: raw?.classesPerWeek ?? null,
    rows,
    facts,
    isEmpty: rows.length === 0 && facts.length === 0 && !raw?.classesPerWeek,
  };
};
